import { useAuth, useClerk, useSession, useUser } from '@clerk/clerk-react'
import { useStatsigClient } from '@statsig/react-bindings'
import {
  IconCheck,
  IconLogout,
  IconSettings,
  IconSettings2,
  IconSettingsFilled,
  IconSpy,
  IconSpyOff,
  IconUserCog,
  IconWorldCog,
} from '@tabler/icons-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { clearShowAppAlertFlag, hasPendingUnsavedNavigation } from '@/utility/showAppAlertStorage'
import { useUserPublicMetadata } from '../../hooks/useUserPublicMetadata'
import { markOrganizationManagementOpenedFromApp } from '../../pages/Organization/orgManagementBackNavigation'
import { markUserManagementOpenedFromApp } from '../../pages/Organization/userManagementBackNavigation'
import {
  getCachedUserOrgStatus,
  loadUserOrgStatus,
  useActiveUserOrgStore,
} from '../../stores/activeUserOrgStore'
import { snapshotCommandCenterViewForOrg } from '../../utility/commandCenterSystemViewPersistence'
import {
  clearShipmentFilterStorage,
  getImpersonationTargetOrgId,
  isSameOrganizationContext,
  markImpersonationTransition,
} from '../../utility/impersonateClerkUser'
import {
  FILTER_STATE_IMPERSONATION_FIX_GATE,
  markImpersonationStopFilterReset,
} from '../../utility/impersonationFilterReset'
import {
  clearOrgSwitch,
  markOrgSwitchTransition,
  ORG_SWITCH_STORAGE_KEY,
} from '../../utility/orgSwitchStorage'
import {
  isKeepCustomViewSelectedAfterRemoveDefaultEnabled,
  RELEASE_GATE_V2_13_0,
} from '../../utility/releaseStatsigGateConstants.js'
import { signOutCurrentUser } from '../../utility/signOutUser'
import { resolveActiveOrganizationIds } from '../../utility/userOrgStatus'
import { clearLocalStorage, getUserUagName } from '../../utility/utility'
import AlertBox from '../AlertBox/AlertBox'
import SmartScroll from '../SmartScroll/SmartScroll'

const UnSavedChanges = ({ showAppAlert, setShowAppAlert, onConfirm }) => {
  return (
    <>
      {showAppAlert && (
        <AlertBox
          title="You have unsaved changes."
          message="Are you sure you want to leave this page? Your changes will be lost."
          confirmBtnText="Confirm"
          confirmBtnType="primary"
          cancelBtnType="secondary-v3"
          onClose={() => {
            setShowAppAlert(false)
          }}
          onConfirm={() => {
            setShowAppAlert(false)
            clearShowAppAlertFlag()
            onConfirm()
          }}
          data-testid="header-user-menu-system-navigate-alert"
        />
      )}
    </>
  )
}

const truncateText = (text = '', maxLength = 40) => {
  if (!text) return ''
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text
}

const OrgSwitcher = ({ organizationMemberships, totalOrgs, showMoreOrgs, setShowMoreOrgs }) => {
  const { isLoaded } = useUser()
  const clerk = useClerk()
  const isLoadedRef = useRef(isLoaded)
  const isMountedRef = useRef(true)
  const { client } = useStatsigClient()
  const uagEnhancements = client.checkGate(RELEASE_GATE_V2_13_0)
  const [showAppAlert, setShowAppAlert] = useState(false)
  const [selectedOrgId, setSelectedOrgId] = useState(null)
  const [isSwitchingOrg, setIsSwitchingOrg] = useState(false)

  useEffect(() => {
    isLoadedRef.current = isLoaded
  }, [isLoaded])

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const waitForUserLoaded = async () => {
    if (isLoadedRef.current) return true

    const maxWaitMs = 5000
    const pollIntervalMs = 50
    let elapsedMs = 0

    while (elapsedMs < maxWaitMs) {
      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs))
      if (isLoadedRef.current) return true
      elapsedMs += pollIntervalMs
    }

    return false
  }

  const redirectToLandingPageAfterOrgSwitch = () => {
    window.location.assign('/landing-page')
  }

  const onOrgClick = async (organizationId) => {
    markOrgSwitchTransition(organizationId)
    if (isSwitchingOrg) return
    setIsSwitchingOrg(true)
    const oldOrgId = clerk.organization?.id
    try {
      snapshotCommandCenterViewForOrg(oldOrgId, clerk.user?.id)
      await clerk.setActive({ organization: organizationId })
      await clerk.session?.touch()
      clearLocalStorage({ preserveOrgCommandCenterViews: true })
      localStorage.setItem(ORG_SWITCH_STORAGE_KEY, organizationId)
      await waitForUserLoaded()

      redirectToLandingPageAfterOrgSwitch()
    } catch (error) {
      console.error('Error switching organization:', error)
      clearOrgSwitch()
      await clerk.setActive({ organization: oldOrgId })
    } finally {
      if (isMountedRef.current) setIsSwitchingOrg(false)
    }
  }

  return (
    <>
      <UnSavedChanges
        showAppAlert={showAppAlert}
        setShowAppAlert={setShowAppAlert}
        onConfirm={() => onOrgClick(selectedOrgId)}
      />
      <div
        className={`flex flex-col w-full ${
          setShowMoreOrgs ? 'border-b border-Border-Default-neutral-01' : ''
        }`}
        data-testid="header-user-menu-org-switcher"
      >
        {organizationMemberships?.map((membership) => (
          <div
            key={membership.organization.id}
            className="cursor-pointer hover:bg-BG-Hover-brand-00 flex items-center justify-between w-full"
            data-testid="header-user-menu-org-item"
            onClick={(e) => {
              e?.preventDefault?.()
              e?.stopPropagation?.()
              if (hasPendingUnsavedNavigation()) {
                setSelectedOrgId(membership.organization.id)
                setShowAppAlert(true)
                return false
              }
              clearShowAppAlertFlag()
              onOrgClick(membership.organization.id)
            }}
          >
            <div className="flex items-center gap-3 p-3">
              <img
                src={membership.organization.imageUrl}
                alt={membership.organization.name}
                className={`${uagEnhancements ? 'w-8 h-8' : 'w-6 h-6'} rounded-full`}
              />
              <div className="flex flex-col gap-1 max-w-[160px]">
                <span
                  className="text-Text-medium-06 text-xs font-bold break-words"
                  title={
                    membership.organization.name?.length > 40
                      ? membership.organization.name
                      : undefined
                  }
                >
                  {truncateText(membership.organization.name)}
                </span>
                {!uagEnhancements && (
                  <span
                    className="text-Text-placeholder text-[10px] font-medium break-words"
                    title={membership.roleName?.length > 40 ? membership.roleName : undefined}
                  >
                    {truncateText(membership.roleName)}
                  </span>
                )}
              </div>
            </div>
            {clerk?.organization?.id === membership.organization.id && (
              <IconCheck className="w-4 h-4 mr-3 text-Misc-brand shrink-0" />
            )}
          </div>
        ))}
        {totalOrgs > 3 && (
          <div
            className="flex items-center p-3 cursor-pointer hover:bg-BG-Hover-brand-00 w-full"
            data-testid="header-user-menu-org-switcher-more"
            onClick={() => setShowMoreOrgs(!showMoreOrgs)}
          >
            <span className="text-Misc-brand text-xs font-bold whitespace-nowrap">
              {showMoreOrgs ? 'Show less' : 'Show more'}
            </span>
          </div>
        )}
      </div>
    </>
  )
}

const UserMenuItem = ({ label, icon, borderBottom, onMenuItemClick, setShowUserMenu }) => {
  const [showAppAlert, setShowAppAlert] = useState(false)

  return (
    <>
      <UnSavedChanges
        showAppAlert={showAppAlert}
        setShowAppAlert={setShowAppAlert}
        onConfirm={onMenuItemClick}
      />
      <div
        className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-BG-Default-neutral-00 ${
          borderBottom ? 'border-b border-Border-Default-neutral-01' : ''
        }`}
        data-testid="header-user-menu-item"
        onClick={(e) => {
          e?.preventDefault?.()
          e?.stopPropagation?.()
          if (hasPendingUnsavedNavigation()) {
            setShowAppAlert(true)
            return false
          }
          clearShowAppAlertFlag()
          onMenuItemClick()
          setShowUserMenu(false)
        }}
      >
        {icon}
        <span className="text-Text-small-04 text-xs font-medium whitespace-nowrap">{label}</span>
      </div>
    </>
  )
}

const UserMenu = ({ userImpersonationOrgs, headerExpand }) => {
  const { user } = useUser()
  const { session } = useSession()
  const { actor } = useAuth()
  const { publicMetadata } = useUserPublicMetadata()
  const clerk = useClerk()
  const navigate = useNavigate()
  const location = useLocation()
  const userMenuRef = useRef(null)
  const { client } = useStatsigClient()

  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showMoreOrgs, setShowMoreOrgs] = useState(false)

  const uagEnhancements = client.checkGate(RELEASE_GATE_V2_13_0)
  const userOrgStatus = useActiveUserOrgStore((state) => state.userOrgStatus)

  useEffect(() => {
    if (!uagEnhancements || !user?.id || !session) return
    if (getCachedUserOrgStatus(user.id)) return

    void loadUserOrgStatus({ session, userId: user.id })
  }, [uagEnhancements, user?.id, session])

  useEffect(() => {
    if (!showUserMenu) return
    const handleClickOutside = (event) => {
      // Ignore clicks that originate from the global AlertBox overlay
      const isAlertClick = event.target.closest('[data-testid="alert-box"]')
      if (isAlertClick) return
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false)
        setShowMoreOrgs(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showUserMenu, showMoreOrgs])

  const customSignOut = () => signOutCurrentUser({ clerk, user })

  const impersonationSignOut = async () => {
    try {
      const impersonationOrgId = getImpersonationTargetOrgId()
      const currentOrgId = clerk?.organization?.id
      const filterStateFixEnabled = client.checkGate(FILTER_STATE_IMPERSONATION_FIX_GATE)
      const shouldResetFiltersOnStop = filterStateFixEnabled
      const storageCleanupOptions = {
        preserveOrgCommandCenterViews: !filterStateFixEnabled,
        preserveTagsCatalog: isSameOrganizationContext(impersonationOrgId, currentOrgId),
      }

      if (isKeepCustomViewSelectedAfterRemoveDefaultEnabled(client)) {
        markImpersonationTransition('stop')
      }

      if (shouldResetFiltersOnStop) {
        markImpersonationStopFilterReset()
      } else {
        clearLocalStorage(storageCleanupOptions)
      }

      const impersonatedSessionId = clerk?.session?.id
      if (!impersonatedSessionId) return

      const sessions = clerk.client?.sessions ?? []
      const rootSession = sessions.find((session) => !session.actor?.sub) ?? null
      const impersonatedSessions = sessions.filter((session) => session.actor?.sub)

      for (const session of impersonatedSessions) {
        await clerk.signOut({ sessionId: session.id })
      }

      if (rootSession) {
        await clerk.setActive({ session: rootSession.id })
      }

      if (shouldResetFiltersOnStop) {
        clearShipmentFilterStorage()
        clearLocalStorage(storageCleanupOptions)
      }

      window.location.replace('/landing-page')
    } catch (error) {
      console.error('Error signing out impersonation:', error)
    }
  }

  const getOrgMembershipsList = (clerkOrgMembershipsData, publicOrgMembershipsData) => {
    return clerkOrgMembershipsData.filter(
      (membership) =>
        !(
          Object.keys(publicOrgMembershipsData).includes(membership?.organization?.id) &&
          publicOrgMembershipsData[membership?.organization?.id]?.suspended === true &&
          publicOrgMembershipsData[membership?.organization?.id]?.partner_org_id !== null
        ),
    )
  }

  const organizationMemberships = useMemo(() => {
    const clerkMemberships = user?.organizationMemberships || []
    let memberships = clerkMemberships

    if (uagEnhancements) {
      const clerkMembershipOrgIds = clerkMemberships
        .map((membership) => membership.organization?.id)
        .filter(Boolean)
      const activeOrgIds = resolveActiveOrganizationIds({
        clerkMembershipOrgIds,
        activeOrgIds: userOrgStatus?.active_org_ids ?? [],
      })
      const membershipByOrgId = new Map(
        clerkMemberships.map((membership) => [membership.organization?.id, membership]),
      )

      memberships = activeOrgIds.map((orgId) => membershipByOrgId.get(orgId)).filter(Boolean)
    }

    memberships = getOrgMembershipsList(memberships, publicMetadata?.org_memberships || {})

    const activeOrgId = clerk?.organization?.id

    if (!activeOrgId) {
      return memberships
    }

    const activeMemberships = memberships.filter(
      (membership) => membership.organization.id === activeOrgId,
    )
    const otherMemberships = memberships.filter(
      (membership) => membership.organization.id !== activeOrgId,
    )

    return [...activeMemberships, ...otherMemberships]
  }, [
    user?.organizationMemberships,
    clerk?.organization?.id,
    publicMetadata,
    uagEnhancements,
    userOrgStatus,
  ])

  return (
    <div className="relative" ref={userMenuRef}>
      <div
        className="flex items-center gap-3 cursor-pointer relative"
        onClick={() => {
          setShowUserMenu(!showUserMenu)
          if (showMoreOrgs) {
            setShowMoreOrgs(false)
          }
        }}
        data-testid="header-user-menu-toggle"
      >
        <img
          alt="user_image"
          src={user?.imageUrl}
          className="w-10 h-10 rounded-full cursor-pointer"
        />
        {actor?.sub && (
          <div className="absolute -top-2.5 right-0 rounded-full h-6 w-6 bg-BG-Default-warning-06 flex items-center justify-center">
            <IconSpy className="w-4 h-4 shrink-0 text-Text-on-color" />
          </div>
        )}
        {headerExpand && (
          <div className="flex flex-col gap-[2px] max-w-[140px]">
            <span
              className="text-Text-medium-06 text-sm font-bold break-words"
              title={
                clerk?.user?.fullName && clerk.user.fullName.length > 40
                  ? clerk.user.fullName
                  : undefined
              }
            >
              {truncateText(clerk?.user?.fullName)}
            </span>
            <span
              className="text-Text-placeholder text-xs font-medium break-words"
              title={
                clerk?.organization?.name && clerk.organization.name.length > 40
                  ? clerk.organization.name
                  : undefined
              }
            >
              {truncateText(clerk?.organization?.name)}
            </span>
          </div>
        )}
      </div>
      {showUserMenu && (
        <div
          className="absolute bottom-12 left-0 z-50 flex flex-col bg-white rounded-md shadow-md border border-Border-Default-neutral-01 min-w-[300px] overflow-hidden"
          data-testid="header-user-menu-dropdown"
        >
          <OrgSwitcher
            organizationMemberships={organizationMemberships?.slice(0, 3)}
            totalOrgs={organizationMemberships?.length}
            showMoreOrgs={showMoreOrgs}
            setShowMoreOrgs={setShowMoreOrgs}
          />
          {uagEnhancements && (
            <>
              {getUserUagName() === 'owner' && (
                <UserMenuItem
                  label="Organization Settings"
                  icon={<IconSettings2 className="w-4 h-4 shrink-0 text-Icon-strong-06" />}
                  borderBottom={true}
                  onMenuItemClick={() => {
                    markOrganizationManagementOpenedFromApp(
                      `${location.pathname}${location.search}`,
                    )
                    navigate(`/organization/${clerk?.organization?.id}/general`)
                  }}
                  setShowUserMenu={setShowUserMenu}
                />
              )}
              <UserMenuItem
                label="User Settings"
                icon={<IconSettings className="w-4 h-4 shrink-0 text-Icon-strong-06" />}
                borderBottom={true}
                onMenuItemClick={() => {
                  markUserManagementOpenedFromApp(`${location.pathname}${location.search}`)
                  navigate(`/user/${clerk?.user?.id}/details`)
                }}
                setShowUserMenu={setShowUserMenu}
              />
            </>
          )}
          {!uagEnhancements && (
            <UserMenuItem
              label="Manage Account"
              icon={<IconSettingsFilled className="w-4 h-4 shrink-0 text-Icon-strong-06" />}
              borderBottom={true}
              onMenuItemClick={() => navigate('/manage/account')}
              setShowUserMenu={setShowUserMenu}
            />
          )}
          {actor?.sub && (
            <UserMenuItem
              label="Stop Impersonating"
              icon={<IconSpyOff className="w-4 h-4 shrink-0 text-Icon-strong-06" />}
              borderBottom={true}
              onMenuItemClick={() => impersonationSignOut()}
              setShowUserMenu={setShowUserMenu}
            />
          )}
          {userImpersonationOrgs && false && (
            <UserMenuItem
              label="User Impersonation"
              icon={
                uagEnhancements ? (
                  <IconSpy className="w-4 h-4 shrink-0 text-Icon-strong-06" />
                ) : (
                  <IconUserCog className="w-4 h-4 shrink-0 text-Icon-strong-06" />
                )
              }
              borderBottom={true}
              onMenuItemClick={() =>
                uagEnhancements
                  ? navigate('/user-impersonation')
                  : navigate('/manage/impersonation')
              }
              setShowUserMenu={setShowUserMenu}
            />
          )}
          {!uagEnhancements && (
            <UserMenuItem
              label="Time zone"
              icon={<IconWorldCog className="w-4 h-4 shrink-0 text-Icon-strong-06" />}
              borderBottom={true}
              onMenuItemClick={() => navigate('/manage/timezone')}
              setShowUserMenu={setShowUserMenu}
            />
          )}
          <UserMenuItem
            label="Sign out"
            icon={<IconLogout className="w-4 h-4 shrink-0 text-Icon-strong-06" />}
            onMenuItemClick={() => customSignOut()}
            setShowUserMenu={setShowUserMenu}
          />
        </div>
      )}
      {showMoreOrgs && (
        <div
          className="absolute bottom-[215px] left-[305px] z-50 flex flex-col bg-white rounded-md shadow-md border border-Border-Default-neutral-01 min-w-[300px]"
          data-testid="header-user-menu-orgs-dropdown"
        >
          <SmartScroll className="w-full" maxHeight="max-h-[calc(100vh-300px)]">
            <OrgSwitcher organizationMemberships={organizationMemberships?.slice(3)} />
          </SmartScroll>
        </div>
      )}
    </div>
  )
}

export default UserMenu
